import { selectNodeAtPos } from '@atlaskit/editor-common/node-selection';
import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { publish } from '@atlaskit/rovo-triggers/main';
import { type OpenRovoRemixSidebarPayload, Topics } from '@atlaskit/rovo-triggers/types';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import type { MalleableUiPlugin } from '../malleableUiPluginType';
import { malleableUiPluginKey } from '../pm-plugins/plugin-key';
import {
	type RemixThinkingPluginMeta,
	remixThinkingPluginKey,
} from '../pm-plugins/remix-thinking/remix-thinking-plugin';
import { publishEditorRemixPreset } from '../publish-editor-remix-preset';
import type { RemixModalMetadata } from '../types';
import { makeSelectRemixAnchor, applyHeadingSectionSelection } from './select-remix-anchor';

export type OpenRemixModalOptions = {
	metadata?: RemixModalMetadata;
	nodePos?: number;
	nodeType?: string;
	source?: string;
};

export const PASTE_MENU_SOURCE = 'pasteMenu';

/**
 * Opens the remix modal and, if a node position and type are provided, updates
 * the editor selection to reflect the clicked node.
 *
 * Uses `tr.selection` as the current selection at dispatch time.
 */
export const makeOpenRemixModal =
	(api: ExtractInjectionAPI<MalleableUiPlugin> | undefined) =>
	({ nodePos, nodeType, metadata, source }: OpenRemixModalOptions = {}): EditorCommand =>
	({ tr }) => {
		// If the remix modal is being opened from the paste menu, set the selection to the pasted content range
		if (
			source === PASTE_MENU_SOURCE &&
			api &&
			'pasteOptionsToolbarPlugin' in api &&
			api.pasteOptionsToolbarPlugin &&
			['hasSpellingAndGrammar', 'hasAltAiActions'].includes(
				expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control'),
			)
		) {
			const pasteState = api.pasteOptionsToolbarPlugin.sharedState.currentState();
			if (!pasteState) {
				// Exit early if we can't get the paste state, which means we won't be able to set the selection to the pasted content.
				return null;
			}
			const { pasteStartPos, pasteEndPos } = pasteState;
			if (pasteStartPos === undefined || pasteEndPos === undefined) {
				// Exit early if the paste state doesn't have valid start or end positions.
				return null;
			}
			const pasteSelection = TextSelection.create(tr.doc, pasteStartPos, pasteEndPos);
			tr.setSelection(pasteSelection);
			api?.selection?.commands?.setBlockSelection(pasteSelection)({ tr });
		} else if (nodePos !== undefined && nodeType) {
			if (
				UNSAFE_expValNoExposure('platform_editor_ai_content_suggested_edits', 'isEnabled', false)
			) {
				// Node-selection logic extracted into `makeSelectRemixAnchor` so it can be reused to
				// anchor a remix without opening the modal (e.g. Suggested Remix).
				makeSelectRemixAnchor(api)({ nodePos, nodeType, source })({ tr });
			} else {
				// TODO(platform_editor_ai_content_suggested_edits cleanup): delete this legacy inline
				// branch — the `makeSelectRemixAnchor` call above is the replacement.
				if (fg('cc-maui-phase-3')) {
					const didApplyHeadingSelection = applyHeadingSectionSelection({
						nodePos,
						source,
						tr,
					});

					if (!didApplyHeadingSelection) {
						selectNodeAtPos(tr, nodePos, nodeType);
					}
				} else if (fg('platform_editor_maui_jira_updates')) {
					selectNodeAtPos(tr, nodePos, nodeType);
				} else {
					api?.blockControls?.commands.expandAndUpdateSelection({
						startPos: nodePos,
						nodeType,
						selection: tr.selection,
						isShiftPressed: false,
					})({ tr });
				}

				// If a multi-block TextSelection was set, signal to the selection plugin
				// so that ak-editor-selected-node decorations are applied to all selected nodes.
				if (tr.selection instanceof TextSelection) {
					api?.selection?.commands?.setBlockSelection(tr.selection)({ tr });
				}
			}
		}

		if (
			!metadata?.userPrompt &&
			UNSAFE_expValNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control') ===
				'variant2'
		) {
			const remixPreset = tr.selection.empty ? 'page' : 'selection';

			const payload: OpenRovoRemixSidebarPayload = {
				type: 'open-rovo-remix-sidebar',
				source: source ?? 'remix',
				consumeOnce: true,
				openChat: true,
				openChatMode: 'sidebar',
				autoFocusInput: false,
				data: {
					remixConsumer: 'editor',
					remixPreset,
				},
			};

			// The same message is deliberately sent on two topics, because two different parts of
			// the product are listening and each one only listens on one of them:
			//
			// - The 'ai-mate' topic is what opens the Rovo sidebar in the first place. It is also
			//   where the RemixContent component in this package listens, so that it can remember
			//   which blocks of the page the user had selected. It needs that later, when the
			//   finished content comes back from Rovo and has to be inserted in the right place.
			// - The 'rovo-remix-launch' topic is what tells the Rovo sidebar to show the list of
			//   Remix options rather than an ordinary chat conversation.
			//
			// Sending on only one of the two topics breaks the other half of the feature. Sending
			// on both is safe: the publish and subscribe system gives every call to publish its own
			// delivery identifier, so a listener that asked to handle a message only once still
			// receives its own copy instead of discarding the second send as a repeat.
			publish(Topics.AI_MATE, payload);
			publish(Topics.ROVO_REMIX_LAUNCH, payload);

			// Send the whole-page-or-selection choice again, on its own topic. The list of Remix
			// options only starts listening once the sidebar has finished rendering, which happens
			// after the two messages above have already been delivered, so without this it would
			// never find out whether the user is remixing the whole page or only their selection.
			publishEditorRemixPreset(tr.selection);

			return tr;
		}

		tr.setMeta(malleableUiPluginKey, {
			isRemixModalOpen: true,
			remixModalMetadata: metadata,
			remixSource: source,
		});

		if (fg('platform_editor_ai_remix_toolbar')) {
			tr.setMeta(remixThinkingPluginKey, {
				presentation: { kind: 'reset' },
				lifecycle: { kind: 'legacy-reset' },
			} satisfies RemixThinkingPluginMeta);
		}
		return tr;
	};
